import React from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { StarIcon, CircleDollarSign } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface UpgradePromptProps {
  title?: string;
  description?: string;
  featuresList?: string[];
  buttonText?: string;
  onUpgradeClick?: (featureName?: string) => void;
  compact?: boolean;
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  title = "Unlock PRO Features",
  description = "Upgrade to our PRO Plan to access advanced features and grow your events.",
  featuresList = [
    "Multiple ticket types",
    "Advanced event analytics",
    "Custom branding options",
    "Priority support",
  ],
  buttonText = "Upgrade to PRO",
  onUpgradeClick,
  compact = false,
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
    } else {
      navigate("/pricing");
    }
  };

  if (compact) {
    return (
      <div className="flex items-center justify-between p-4 bg-cecure-50 border border-cecure-200 rounded-md">
        <div className="flex items-center">
          <StarIcon className="h-5 w-5 text-cecure-700 mr-2" />
          <p className="text-sm font-medium">PRO feature</p>
        </div>
        <Button size="sm" onClick={handleClick}>
          Upgrade
        </Button>
      </div>
    );
  }

  return (
    <Card className="border-cecure-200 shadow-md">
      <CardHeader className="bg-gradient-to-r from-cecure-700 to-cecure-900 text-white">
        <div className="flex items-center mb-2">
          <StarIcon className="h-6 w-6 mr-2" />
          <CardTitle>{title}</CardTitle>
        </div>
        <CardDescription className="text-cecure-100">
          {description}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <h3 className="font-medium mb-3">PRO Plan includes:</h3>
        <ul className="space-y-2">
          {featuresList.map((feature, index) => (
            <li key={index} className="flex items-start">
              <CircleDollarSign className="h-5 w-5 text-cecure-700 mr-2 flex-shrink-0 mt-0.5" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>
      <CardFooter>
        <Button
          className="w-full bg-cecure-700 hover:bg-cecure-800"
          onClick={handleClick}
        >
          {buttonText}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default UpgradePrompt;
